/**
 * Institution → Team (M1 completion surface).
 *
 * Tenant-confined staff management. Consumes the existing M1 backend
 * endpoints under /api/institution/staff:
 *   - GET    /                      list staff (own tenant only)
 *   - POST   /invite                invite institution_admin or institution_staff
 *   - POST   /{user_id}/disable     soft-disable a seat
 *   - POST   /{user_id}/enable      re-enable a previously disabled seat
 *
 * Access: the backend gates every endpoint on the
 * `institution:manage_staff` permission (system_admin, institution_admin).
 * institution_staff will hit 403 on the underlying calls; the UI degrades
 * gracefully into a read-only banner in that case so it never lies about
 * disabled capabilities.
 *
 * No new user, RBAC, or tenant primitives — reuses the existing
 * password_reset_tokens activation flow (48h TTL) already introduced
 * by staff.py.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const ROLE_LABEL = {
  institution_admin: "Admin",
  institution_staff: "Staff",
};

function StateBadge({ user }) {
  if (user.disabled) {
    return (
      <span className="vlk-portal-role-pill" data-testid={`team-state-disabled-${user.user_id}`}
            style={{ background: "rgba(239,68,68,0.15)", color: "#fca5a5" }}>
        Disabled
      </span>
    );
  }
  if (user.activation_state === "PENDING_ACTIVATION") {
    return (
      <span className="vlk-portal-role-pill" data-testid={`team-state-pending-${user.user_id}`}
            style={{ background: "rgba(234,179,8,0.15)", color: "#fde68a" }}>
        Pending activation
      </span>
    );
  }
  return (
    <span className="vlk-portal-role-pill" data-testid={`team-state-active-${user.user_id}`}
          style={{ background: "rgba(34,197,94,0.15)", color: "#86efac" }}>
      Active
    </span>
  );
}

export default function InstitutionTeam() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "institution_staff" });

  const canManage = user?.role === "system_admin" || user?.role === "institution_admin";

  const listQuery = useQuery({
    queryKey: ["institution-staff"],
    queryFn: () => api.get("/institution/staff").then((r) => r.data),
    enabled: canManage,
    retry: false,
  });

  const invite = useMutation({
    mutationFn: (payload) => api.post("/institution/staff/invite", payload).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Invitation sent to ${data.email}`);
      setInviteOpen(false);
      setForm({ email: "", name: "", role: "institution_staff" });
      qc.invalidateQueries({ queryKey: ["institution-staff"] });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === "string" ? detail : (detail?.message || "Invite failed");
      toast.error(message);
    },
  });

  const toggle = useMutation({
    mutationFn: ({ user_id, disabled }) =>
      api.post(`/institution/staff/${user_id}/${disabled ? "disable" : "enable"}`,
               { reason: disabled ? "Disabled from Team surface" : "Re-enabled from Team surface" })
         .then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Staff ${data.disabled ? "disabled" : "enabled"}`);
      qc.invalidateQueries({ queryKey: ["institution-staff"] });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === "string" ? detail : (detail?.message || "Update failed");
      toast.error(message);
    },
  });

  if (!canManage) {
    return (
      <div className="max-w-4xl" data-testid="institution-team-readonly">
        <h1 className="text-2xl font-semibold text-white mb-2">Team &amp; Access</h1>
        <div className="rounded border border-white/10 bg-white/5 p-6 text-white/70">
          Your role ({user?.role}) does not permit staff management. Ask an
          institution administrator to invite or manage seats.
        </div>
      </div>
    );
  }

  const items = listQuery.data?.items || [];
  return (
    <div className="max-w-5xl" data-testid="institution-team-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white">Team &amp; Access</h1>
          <p className="text-sm text-white/60 mt-1">
            Invite and manage administrators and staff for your institution.
            Each invite creates a 48-hour activation link.
          </p>
        </div>
        <Button
          onClick={() => setInviteOpen(true)}
          data-testid="team-invite-button"
          className="bg-emerald-500/90 hover:bg-emerald-500 text-black font-medium"
        >
          Invite member
        </Button>
      </div>

      {listQuery.isLoading && (
        <div className="text-white/50 text-sm" data-testid="team-loading">Loading team…</div>
      )}
      {listQuery.isError && (
        <div className="text-red-300 text-sm" data-testid="team-error">
          Failed to load team: {listQuery.error?.response?.data?.detail?.message || listQuery.error.message}
        </div>
      )}

      {listQuery.isSuccess && (
        <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="team-table">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/60 uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Role</th>
                <th className="text-left px-4 py-2">State</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-white/50 text-center" colSpan={5}>
                    No team members yet. Click <em>Invite member</em> to get started.
                  </td>
                </tr>
              )}
              {items.map((u) => (
                <tr key={u.user_id} className="border-t border-white/5" data-testid={`team-row-${u.user_id}`}>
                  <td className="px-4 py-3 text-white/90">{u.email}</td>
                  <td className="px-4 py-3 text-white/70">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-white/70">{ROLE_LABEL[u.role] || u.role}</td>
                  <td className="px-4 py-3"><StateBadge user={u} /></td>
                  <td className="px-4 py-3 text-right">
                    {u.user_id === user?.user_id ? (
                      <span className="text-xs text-white/40">You</span>
                    ) : u.disabled ? (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => toggle.mutate({ user_id: u.user_id, disabled: false })}
                        disabled={toggle.isPending}
                        data-testid={`team-enable-${u.user_id}`}
                      >
                        Enable
                      </Button>
                    ) : (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => toggle.mutate({ user_id: u.user_id, disabled: true })}
                        disabled={toggle.isPending}
                        data-testid={`team-disable-${u.user_id}`}
                      >
                        Disable
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="team-invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite team member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="name@company.com"
                data-testid="team-invite-email"
              />
            </div>
            <div>
              <Label htmlFor="invite-name">Full name</Label>
              <Input
                id="invite-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe"
                data-testid="team-invite-name"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="team-invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="institution_staff">Staff — read + operational actions</SelectItem>
                  <SelectItem value="institution_admin">Admin — full institution access</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} data-testid="team-invite-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => invite.mutate(form)}
              disabled={!form.email || !form.name || invite.isPending}
              data-testid="team-invite-submit"
              className="bg-emerald-500/90 hover:bg-emerald-500 text-black"
            >
              {invite.isPending ? "Sending…" : "Send invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
